import React, { RefObject, useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { twMerge } from 'tailwind-merge'
import { ButtonProps } from 'shared/components/button/types/button-interface'
import { OrderEnum, PaginationInterface } from 'shared/types/swr-pagination-interface'
import { TemplatesApiInterface } from '../types/templates-interface'

export type TemplatesLimitType = 15 | 30 | 60

const limitArray: TemplatesLimitType[] = [15, 30, 60]

export interface TemplatesPaginationProps {
  data?: TemplatesApiInterface
  limiter: {
    limit: TemplatesLimitType
    setLimit: React.Dispatch<React.SetStateAction<TemplatesLimitType>>
  }
  paginator: {
    pagination: PaginationInterface
    setPagination: React.Dispatch<React.SetStateAction<PaginationInterface>>
  }
  templatesRef: RefObject<HTMLDivElement>
}

const TemplatesPagination = <T extends { id: number }>({
  data,
  limiter,
  paginator,
  templatesRef,
}: TemplatesPaginationProps) => {
  const { t } = useTranslation()
  const { pagination, setPagination } = paginator
  const { limit, setLimit } = limiter

  const onPaginationClick = () => {
    if (templatesRef.current) {
      templatesRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const [isPaginationShown, setIsPaginationShown] = useState(true)

  const [isFirst, setIsFirst] = useState(true)

  const [isLast, setIsLast] = useState(false)

  useEffect(() => {
    if (!isFirst && data?.items.length === 0) {
      setPagination({ order: OrderEnum.next })
    }
  }, [data?.items.length, isFirst, setPagination])

  useEffect(() => {
    if (data) {
      setIsFirst(true)
      setIsLast(false)

      switch (pagination.order) {
        case 'next': {
          if (pagination.startFromId) {
            setIsFirst(false)
          }
          if (!data.hasMore) {
            setIsLast(true)
          }
          break
        }
        case 'prev': {
          if (data.hasMore) {
            setIsFirst(false)
          }
        }
      }
    }
  }, [data, pagination.order, pagination.startFromId])

  useEffect(() => {
    if (data) {
      setIsPaginationShown(true)

      if (!isFirst || limiter.limit !== 15) return

      if (pagination.order === 'next' && !data.hasMore) {
        setIsPaginationShown(false)
      }
      if (data.items.length === 0) {
        setIsPaginationShown(false)
      }
    } else {
      setIsPaginationShown(false)
    }
  }, [data, isFirst, limiter.limit, pagination.order, pagination.startFromId])

  return isPaginationShown ? (
    <div className={`flex items-center justify-end gap-5 sm:gap-10`}>
      <div className={`flex gap-2`}>
        {limitArray.map(el => (
          <button
            key={el}
            className={twMerge(
              'flex size-9 cursor-pointer items-center justify-center rounded-full text-sm font-bold outline-none ring-brand-primary ring-offset-1 focus-visible:ring-2',
              limit === el
                ? 'bg-background-tertiary text-brand-primary'
                : 'bg-transparent text-text-secondary',
            )}
            onClick={() => {
              setLimit(el)
              setPagination({
                startFromId: undefined,
                order: OrderEnum.next,
              })
              onPaginationClick()
            }}
          >
            {el}
          </button>
        ))}
      </div>
      <div className={`flex gap-3`}>
        <PaginationButton
          disabled={isFirst}
          onClick={() => {
            const id = data?.items[0]?.id
            setPagination({
              startFromId: id,
              order: OrderEnum.prev,
            })
            onPaginationClick()
          }}
        >
          {t('global.previous')}
        </PaginationButton>
        <PaginationButton
          disabled={isLast}
          onClick={() => {
            const id = data?.items[data.items.length - 1]?.id
            setPagination({
              startFromId: id,
              order: OrderEnum.next,
            })
            onPaginationClick()
          }}
        >
          {t('global.next')}
        </PaginationButton>
      </div>
    </div>
  ) : null
}

const PaginationButton = (props: ButtonProps) => (
  <button
    disabled={props.disabled}
    className={twMerge(
      'main-transition-colors relative flex min-h-[42px] items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-text-secondary/30 px-4 py-2.5 font-ubuntu text-sm font-semibold text-text-primary/50 outline-none ring-brand-primary ring-offset-1 hover:border-brand-primary hover:bg-brand-primary hover:text-background-secondary focus-visible:ring-2 [&>*]:font-ubuntu [&>*]:font-semibold',
      props.disabled
        ? 'pointer-events-none border-background-tertiary/50 bg-background-tertiary/50'
        : 'bg-background-primary',
    )}
    onClick={props.onClick}
  >
    {props.children}
  </button>
)

export default TemplatesPagination
